import React, { useState, useEffect } from 'react';
import { 
  X, 
  Send, 
  Users, 
  GraduationCap, 
  Bell,
  Loader2,
  CheckCircle,
  AlertCircle,
  History,
  Clock,
  ChevronDown,
  ChevronUp,
  User
} from 'lucide-react';
import { collection, query, where, getDocs, orderBy, limit } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../../firebase/config';
import toast from 'react-hot-toast';

const SendNotificationModal = ({ isOpen, onClose, coachingId }) => {
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [targetAudience, setTargetAudience] = useState('all');
  const [selectedBatches, setSelectedBatches] = useState([]);
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingBatches, setLoadingBatches] = useState(true);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [previewUsers, setPreviewUsers] = useState([]);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  useEffect(() => {
    if (isOpen && coachingId) {
      fetchBatches();
      fetchHistory();
      fetchPreviewUsers();
    }
  }, [isOpen, coachingId, targetAudience, selectedBatches]);

  const fetchPreviewUsers = async () => {
    if (!coachingId) return;
    setLoadingPreview(true);
    try {
      let users = [];
      const usersRef = collection(db, 'users');
      const studentsRef = collection(db, 'students');
      const enrollmentsRef = collection(db, 'batchEnrollments');
      const batchesRef = collection(db, 'batches');

      // Fetch all batches for mapping names
      const batchesSnapshot = await getDocs(query(batchesRef, where('coachingId', '==', coachingId)));
      const batchesMap = {};
      batchesSnapshot.docs.forEach(doc => {
        batchesMap[doc.id] = doc.data().name || 'Unnamed Batch';
      });

      if (targetAudience === 'all' || targetAudience === 'students') {
        // Fetch students from 'students' collection (Student Management)
        const sSnapshot = await getDocs(query(studentsRef, where('coachingId', '==', coachingId)));
        let studentsData = sSnapshot.docs.map(doc => ({
          id: doc.id,
          name: doc.data().name || 'Unknown',
          email: doc.data().email?.toLowerCase()?.trim() || 'No email',
          role: 'student',
          batches: [],
          isRegistered: false // New field to track if student has an app account
        }));

        // Fetch all active enrollments for these students
        const eSnapshot = await getDocs(query(enrollmentsRef, where('coachingId', '==', coachingId), where('status', '==', 'active')));
        const studentBatchesMap = {};
        const registeredEmailSet = new Set();
        
        eSnapshot.docs.forEach(doc => {
          const data = doc.data();
          const email = data.email?.toLowerCase()?.trim();
          if (email) {
            if (!studentBatchesMap[email]) studentBatchesMap[email] = [];
            if (batchesMap[data.batchId]) {
              studentBatchesMap[email].push(batchesMap[data.batchId]);
            }
            if (data.userId) registeredEmailSet.add(email);
          }
        });

        // Search users collection for these emails to see who is registered
        const emailsToSearch = studentsData.map(s => s.email).filter(e => e !== 'No email');
        if (emailsToSearch.length > 0) {
          // Firestore 'in' limit is 10
          for (let i = 0; i < emailsToSearch.length; i += 10) {
            const chunk = emailsToSearch.slice(i, i + 10);
            const uSnapshot = await getDocs(query(usersRef, where('email', 'in', chunk)));
            uSnapshot.docs.forEach(doc => {
              const email = doc.data().email?.toLowerCase()?.trim();
              if (email) registeredEmailSet.add(email);
            });
          }
        }

        // Map batches and registration status to students
        studentsData = studentsData.map(s => ({
          ...s,
          batches: studentBatchesMap[s.email] || [],
          isRegistered: registeredEmailSet.has(s.email)
        }));

        // Filter by selected batches if any
        if (selectedBatches.length > 0) {
          const selectedBatchNames = selectedBatches.map(id => batchesMap[id]);
          studentsData = studentsData.filter(s => 
            s.batches.some(bName => selectedBatchNames.includes(bName))
          );
        }

        users = [...studentsData];
      }

      if (targetAudience === 'all' || targetAudience === 'teachers') {
        const q = query(
          usersRef, 
          where('coachingId', '==', coachingId),
          where('role', '==', 'teacher')
        );
        const snapshot = await getDocs(q);
        const teachersData = snapshot.docs.map(doc => ({
          id: doc.id,
          name: doc.data().fullName || doc.data().name || doc.data().displayName || 'Unknown',
          email: doc.data().email || 'No email',
          role: 'teacher',
          batches: [] // Teachers might be in batches too, but usually instructors
        }));
        users = [...users, ...teachersData];
      }

      setPreviewUsers(users);
    } catch (error) {
      console.error('Error fetching preview users:', error);
    } finally {
      setLoadingPreview(false);
    }
  };

  const fetchBatches = async () => {
    setLoadingBatches(true);
    try {
      const batchesRef = collection(db, 'batches');
      const q = query(batchesRef, where('coachingId', '==', coachingId));
      const snapshot = await getDocs(q);
      const batchList = snapshot.docs.map(doc => ({
        id: doc.id,
        name: doc.data().name || 'Unnamed Batch'
      }));
      setBatches(batchList);
    } catch (error) {
      console.error('Error fetching batches:', error);
      toast.error('Failed to load batches');
    } finally {
      setLoadingBatches(false);
    }
  };

  const fetchHistory = async () => {
    setLoadingHistory(true);
    try {
      const logsRef = collection(db, 'notificationLogs');
      const q = query(
        logsRef, 
        where('coachingId', '==', coachingId),
        orderBy('createdAt', 'desc'),
        limit(10)
      );
      const snapshot = await getDocs(q);
      const historyList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate?.() || new Date()
      }));
      setHistory(historyList);
    } catch (error) {
      console.error('Error fetching notification history:', error);
      // If it's an index error, show a helpful message
      if (error.message?.includes('index')) {
        console.error('Firestore index required. Check console for index creation link.');
        toast.error('Setting up notification history... Please check browser console for setup link.');
      }
    } finally {
      setLoadingHistory(false);
    }
  };

  const handleSend = async () => {
    if (!title.trim() || !body.trim()) {
      toast.error('Please enter both title and message');
      return;
    }

    setLoading(true);
    setResult(null);

    try {
      const sendBulkNotification = httpsCallable(functions, 'sendBulkNotification');
      const response = await sendBulkNotification({
        coachingId,
        title: title.trim(),
        body: body.trim(),
        targetAudience,
        batchIds: selectedBatches
      });

      setResult(response.data);
      const userCount = response.data.totalUsers || 0;
      const pushCount = response.data.pushSent || 0;
      const usersWithoutTokens = response.data.usersWithoutTokens || 0;
      
      if (userCount === 0) {
        toast.error('No registered users found to send notification to');
      } else {
        let message = `Notification sent to ${userCount} registered user${userCount > 1 ? 's' : ''}!`;
        if (pushCount > 0) {
          message += ` ${pushCount} push notification${pushCount > 1 ? 's' : ''} delivered.`;
        }
        if (usersWithoutTokens > 0) {
          message += ` (${usersWithoutTokens} user${usersWithoutTokens > 1 ? 's' : ''} will see it in-app only)`;
        }
        toast.success(message);
      }
      
      // Refresh history after sending
      fetchHistory();
      
      // Reset form after success
      setTimeout(() => {
        setTitle('');
        setBody('');
        setTargetAudience('all');
        setSelectedBatches([]);
        setResult(null);
      }, 2000);
    } catch (error) {
      console.error('Error sending notification:', error);
      toast.error(error.message || 'Failed to send notification');
    } finally {
      setLoading(false);
    }
  };

  const toggleBatch = (batchId) => {
    setSelectedBatches(prev => 
      prev.includes(batchId) 
        ? prev.filter(id => id !== batchId)
        : [...prev, batchId]
    );
  };

  const formatDate = (date) => {
    const now = new Date();
    const diff = now - date;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    return date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const getAudienceLabel = (audience) => {
    switch (audience) {
      case 'students': return 'Students';
      case 'teachers': return 'Teachers';
      default: return 'Everyone';
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-xl">
              <Bell className="w-5 h-5 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900 dark:text-white">Send Notification</h2>
              <p className="text-xs text-gray-500 dark:text-gray-400">Push notification to app users</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="p-5 space-y-5 overflow-y-auto max-h-[calc(90vh-180px)]">
          {/* Target Audience */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
              Send To
            </label>
            <div className="grid grid-cols-3 gap-2">
              {[
                { id: 'all', label: 'Everyone', icon: Users },
                { id: 'students', label: 'Students', icon: GraduationCap },
                { id: 'teachers', label: 'Teachers', icon: Users }
              ].map(option => (
                <button
                  key={option.id}
                  onClick={() => setTargetAudience(option.id)}
                  className={`flex flex-col items-center p-3 rounded-xl border-2 transition-all ${
                    targetAudience === option.id
                      ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400'
                      : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                  }`}
                >
                  <option.icon className="w-5 h-5 mb-1" />
                  <span className="text-xs font-semibold">{option.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Batch Selection (Optional) */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
              Filter by Batches (Optional)
            </label>
            {loadingBatches ? (
              <div className="flex items-center justify-center py-4">
                <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
              </div>
            ) : batches.length === 0 ? (
              <p className="text-sm text-gray-500 dark:text-gray-400 py-2">No batches found</p>
            ) : (
              <div className="flex flex-wrap gap-2 max-h-32 overflow-y-auto p-2 bg-gray-50 dark:bg-gray-900/50 rounded-xl">
                {batches.map(batch => (
                  <button
                    key={batch.id}
                    onClick={() => toggleBatch(batch.id)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                      selectedBatches.includes(batch.id)
                        ? 'bg-blue-500 text-white'
                        : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-700 hover:border-blue-300'
                    }`}
                  >
                    {batch.name}
                  </button>
                ))}
              </div>
            )}
            {selectedBatches.length === 0 && (
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Leave empty to send to all batches
              </p>
            )}
          </div>

          {/* Title */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
              Notification Title
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g., Important Announcement"
              maxLength={100}
              className="w-full px-4 py-3 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
            />
            <p className="text-xs text-gray-400 mt-1 text-right">{title.length}/100</p>
          </div>

          {/* Body */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
              Message
            </label>
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder="Enter your notification message..."
              rows={4}
              maxLength={500}
              className="w-full px-4 py-3 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all resize-none"
            />
            <p className="text-xs text-gray-400 mt-1 text-right">{body.length}/500</p>
          </div>

          {/* Targeted Users Preview */}
          <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
            <button
              onClick={() => setShowPreview(!showPreview)}
              className="flex items-center justify-between w-full text-left"
            >
              <div className="flex items-center space-x-2">
                <User className="w-4 h-4 text-gray-500" />
                <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                  Targeted Users Preview
                </span>
                {!loadingPreview && (
                  <div className="flex items-center space-x-1">
                    <span className="px-2 py-0.5 text-xs font-bold bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400 rounded-full" title="Registered Users (will receive notification)">
                      {previewUsers.filter(u => u.role === 'teacher' || u.isRegistered).length} Registered
                    </span>
                    {previewUsers.some(u => u.role === 'student' && !u.isRegistered) && (
                      <span className="px-2 py-0.5 text-xs font-bold bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 rounded-full" title="Unregistered Students (no app account yet)">
                        {previewUsers.filter(u => u.role === 'student' && !u.isRegistered).length} Not Registered
                      </span>
                    )}
                  </div>
                )}
              </div>
              {showPreview ? (
                <ChevronUp className="w-4 h-4 text-gray-500" />
              ) : (
                <ChevronDown className="w-4 h-4 text-gray-500" />
              )}
            </button>

            {showPreview && (
              <div className="mt-3 space-y-2">
                {loadingPreview ? (
                  <div className="flex items-center justify-center py-4">
                    <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
                  </div>
                ) : previewUsers.length === 0 ? (
                  <p className="text-sm text-gray-500 dark:text-gray-400 py-3 text-center">
                    No users targeted with current filters
                  </p>
                ) : (
                  <div className="space-y-2 max-h-48 overflow-y-auto pr-2 custom-scrollbar">
                    {previewUsers.map((user) => (
                      <div
                        key={user.id}
                        className="flex items-center justify-between p-2 bg-gray-50 dark:bg-gray-900/50 rounded-lg border border-gray-100 dark:border-gray-700"
                      >
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-semibold text-gray-900 dark:text-white truncate">
                            {user.name}
                          </p>
                          <p className="text-[10px] text-gray-500 dark:text-gray-400 truncate">
                            {user.email}
                          </p>
                          {user.batches && user.batches.length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-1">
                              {user.batches.map((batch, idx) => (
                                <span key={idx} className="text-[8px] bg-blue-50 dark:bg-blue-900/20 text-blue-500 px-1 rounded">
                                  {batch}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                        <span className={`flex-shrink-0 text-[9px] font-bold px-1.5 py-0.5 rounded-full uppercase ${
                          user.role === 'teacher' 
                            ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400'
                            : !user.isRegistered
                              ? 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400'
                              : 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400'
                        }`}>
                          {user.role === 'teacher' ? 'teacher' : user.isRegistered ? 'student' : 'unregistered'}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Result */}
          {result && (
            <div className={`p-4 rounded-xl ${
              result.success 
                ? 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800' 
                : 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800'
            }`}>
              <div className="flex items-start space-x-3">
                {result.success ? (
                  <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" />
                ) : (
                  <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
                )}
                <div className="text-sm">
                  <p className={`font-semibold ${result.success ? 'text-green-800 dark:text-green-200' : 'text-red-800 dark:text-red-200'}`}>
                    {result.success ? 'Notification Sent!' : 'Failed to Send'}
                  </p>
                  {result.success && (
                    <p className="text-green-700 dark:text-green-300 mt-1">
                      Sent to {result.totalUsers} users • {result.pushSent} push notifications delivered
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Notification History */}
          <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
            <button
              onClick={() => setShowHistory(!showHistory)}
              className="flex items-center justify-between w-full text-left"
            >
              <div className="flex items-center space-x-2">
                <History className="w-4 h-4 text-gray-500" />
                <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                  Recent Notifications
                </span>
                {history.length > 0 && (
                  <span className="px-2 py-0.5 text-xs font-bold bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-400 rounded-full">
                    {history.length}
                  </span>
                )}
              </div>
              {showHistory ? (
                <ChevronUp className="w-4 h-4 text-gray-500" />
              ) : (
                <ChevronDown className="w-4 h-4 text-gray-500" />
              )}
            </button>

            {showHistory && (
              <div className="mt-3 space-y-2">
                {loadingHistory ? (
                  <div className="flex items-center justify-center py-4">
                    <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
                  </div>
                ) : history.length === 0 ? (
                  <p className="text-sm text-gray-500 dark:text-gray-400 py-3 text-center">
                    No notifications sent yet
                  </p>
                ) : (
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {history.map((item) => (
                      <div
                        key={item.id}
                        className="p-3 bg-gray-50 dark:bg-gray-900/50 rounded-xl border border-gray-100 dark:border-gray-700"
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">
                              {item.title}
                            </p>
                            <p className="text-xs text-gray-600 dark:text-gray-400 line-clamp-2 mt-0.5">
                              {item.body}
                            </p>
                          </div>
                          <div className="flex-shrink-0 text-right">
                            <span className="text-[10px] font-semibold text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/30 px-2 py-0.5 rounded-full">
                              {getAudienceLabel(item.targetAudience)}
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center justify-between mt-2 pt-2 border-t border-gray-200 dark:border-gray-700">
                          <div className="flex items-center space-x-1 text-xs text-gray-500 dark:text-gray-400">
                            <Clock className="w-3 h-3" />
                            <span>{formatDate(item.createdAt)}</span>
                          </div>
                          <div className="flex items-center space-x-2 text-xs">
                            <span className="text-green-600 dark:text-green-400 font-semibold">
                              {item.totalUsers || 0} users
                            </span>
                            <span className="text-gray-400">•</span>
                            <span className="text-gray-500 dark:text-gray-400">
                              {item.pushSent || 0} push
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end space-x-3 p-5 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50">
          <button
            onClick={onClose}
            disabled={loading}
            className="px-5 py-2.5 rounded-xl text-sm font-semibold text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSend}
            disabled={loading || !title.trim() || !body.trim()}
            className="flex items-center space-x-2 px-5 py-2.5 rounded-xl text-sm font-semibold bg-blue-600 text-white hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-blue-500/25"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                <span>Sending...</span>
              </>
            ) : (
              <>
                <Send className="w-4 h-4" />
                <span>Send Notification</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SendNotificationModal;
